import {
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { RequirementDetailsContent } from "../../../../Requirements/components/RequirementsDetails/RequirementDetailsContent";
import { RequirementContextType } from "../../../../Requirements/types";
import { useRequirementsConfig } from "../../../../Requirements/useRequirementsConfig";
import {
  PATH_EMBEDDED_ONBOARDING,
  PATH_EMBEDDED_ONBOARDING_REQUIREMENT_DETAILS
} from "../../../../Embedded/paths";
import { PATH_ONBOARDING_PAYEE_REQUIREMENT_DETAILS } from "../../../index";

type RequirementsDetailsProps = {
  requirement: IRequirementResponse;
};

export const RouteOnboardingRequirementDetailsContent: React.FC<
  RequirementsDetailsProps
> = ({ requirement }) => {
  const history = useHistory();
  const location = useLocation();
  const isEmbedded = !!useRouteMatch(
    PATH_EMBEDDED_ONBOARDING_REQUIREMENT_DETAILS
  );

  const requirementsPath = isEmbedded
    ? `${PATH_EMBEDDED_ONBOARDING}/requirements${location.search}`
    : "/member/onboarding/payee/requirements";

  const goBack = useCallback(() => {
    history.replace(requirementsPath);
  }, [history, requirementsPath]);

  const requirementConfig = useRequirementsConfig({
    requirements: [requirement],
    contextType: RequirementContextType.Payer
  });

  const headerActions =
    requirementConfig.getOnboardingToolbarActions(requirement)?.(goBack) || [];

  const title = requirementConfig.getRequirementName(requirement);

  const handleDocumentKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        goBack();
      }
    },
    [goBack]
  );

  useEffect(() => {
    document.addEventListener("keyup", handleDocumentKeyUp);
    return () => {
      document.removeEventListener("keyup", handleDocumentKeyUp);
    };
  }, [handleDocumentKeyUp]);

  const progressSteps =
    requirementConfig.getRequirementProgressSteps(requirement);
  const isCompleteStepDone = progressSteps.some(
    step => step.title === "Complete" && step.status === "Complete"
  );

  const actions = useMemo(() => {
    const result: WSPageToolbarAction[] = [...headerActions];

    if (isCompleteStepDone) {
      result.push({
        label: "Continue",
        buttonKind: "Primary",
        hideButtonOn: [],
        onClick: goBack
      });
    }

    return result;
  }, [headerActions, isCompleteStepDone, goBack]);

  return (
    <WSPage>
      <WSPageToolbar title={title} actions={actions} />
      <RequirementDetailsContent
        {...requirementConfig}
        key={requirementConfig.dataUpdatedAt}
        contextType={RequirementContextType.Payer}
        requirement={requirement}
        showTitle={false}
      />
    </WSPage>
  );
};
